import { formatPeriodLong } from '@commissionsight/shared';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../api.js';
import { PageTabs, chargebacksTabs } from '../components/PageTabs.js';
import { PeriodPicker } from '../components/PeriodPicker.js';
import { WorkspaceFilter } from '../components/WorkspaceFilter.js';
import { Button, Card, EmptyState, Field, ICONS, Loading, inputClass } from '../components/ui.js';
import { formatCurrency } from '../lib/status.js';
import { useCarriers } from '../lib/useCarriers.js';

type ProofVerdict = 'paid' | 'paid_after' | 'never_paid';

interface ProofRow {
  period: string;
  memberRefId: string;
  policyRefId: string;
  memberExternalId: string | null;
  memberName: string | null;
  policyNumber: string | null;
  planName: string | null;
  carrierId: string;
  carrierName: string | null;
  chargebackAmount: number;
  verdict: ProofVerdict;
  originalPayout: {
    period: string;
    amount: number;
    fileId: string | null;
    fileName: string | null;
  } | null;
  reversal: 'full' | 'partial' | 'over' | null;
  policyTotals: { paid: number; chargedBack: number; net: number; paymentCount: number };
}

interface ProofSummary {
  chargebacks: number;
  clawedBack: number;
  provenPaid: number;
  paidAfter: number;
  neverPaid: number;
  fullyReversed: number;
  overReversed: number;
  policies: number;
  from: string | null;
  to: string | null;
  truncated: boolean;
}

/** Grid page size — summary tiles and the CSV export always cover the whole range. */
const PAGE_SIZE = 100;

const VERDICT_BADGE: Record<ProofVerdict, { label: string; className: string }> = {
  paid: {
    label: 'paid first',
    className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  },
  paid_after: {
    label: 'paid only after',
    className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  },
  never_paid: {
    label: 'never paid',
    className: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
  },
};

/**
 * Chargeback proof-out — every clawback across the loaded history traced to its
 * original payment (or flagged never-paid), with per-policy paid-vs-clawed totals
 * and a carrier-ready CSV export. Entitlement-gated (`chargebackProof`).
 */
/** YYYY-MM three calendar months back (inclusive of the current month) — the
 *  default window. A six-year book scanned unbounded made this page crawl;
 *  clearing the range explicitly requests all-time. */
const DEFAULT_FROM = (() => {
  const d = new Date();
  const idx = d.getUTCFullYear() * 12 + d.getUTCMonth() - 2;
  return `${Math.floor(idx / 12)}-${String((idx % 12) + 1).padStart(2, '0')}`;
})();

export function ChargebackProof() {
  const { baseUrl, token, entitlements } = useAuth();
  const { carriers } = useCarriers();
  // Deep links (e.g. from an Ask AI answer) carry the exact filters the numbers
  // were computed with.
  const [urlParams] = useSearchParams();
  const [carrierId, setCarrierId] = useState(urlParams.get('carrierId') ?? '');
  const [workspaceId, setWorkspaceId] = useState('');
  const [from, setFrom] = useState(urlParams.get('from') ?? DEFAULT_FROM);
  const [to, setTo] = useState(urlParams.get('to') ?? '');
  const [summary, setSummary] = useState<ProofSummary | null>(null);
  const [rows, setRows] = useState<ProofRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  // Grid pagination — the summary tiles and the CSV export ALWAYS cover the whole
  // range; only the table pages (a big book would otherwise render thousands of rows).
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);

  // Filter query string, shared by the JSON load and the CSV export. The CSV url
  // deliberately carries NO limit/offset — the export is always the entire list.
  const params = new URLSearchParams();
  if (carrierId) params.set('carrierId', carrierId);
  if (workspaceId) params.set('workspaceId', workspaceId);
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  // Empty pickers = the user explicitly cleared the range → all-time (otherwise
  // the API would re-apply its own 3-month default).
  if (!from && !to) params.set('range', 'all');
  const qs = params.toString();
  const proofUrl = `${baseUrl.replace(/\/$/, '')}/chargebacks/proof?${qs ? `${qs}&` : ''}limit=${PAGE_SIZE}&offset=${offset}`;
  const csvUrl = `${baseUrl.replace(/\/$/, '')}/chargebacks/proof?${qs ? `${qs}&` : ''}format=csv`;

  // Changing any filter resets to the first page — the setters below wrap the
  // raw state setters so a stale offset can never survive a filter change.
  const changeCarrier = (v: string) => {
    setCarrierId(v);
    setOffset(0);
  };
  const changeWorkspace = (v: string) => {
    setWorkspaceId(v);
    setOffset(0);
  };
  const changeFrom = (v: string) => {
    setFrom(v);
    setOffset(0);
  };
  const changeTo = (v: string) => {
    setTo(v);
    setOffset(0);
  };

  const enabled = entitlements.chargebackProof;
  useEffect(() => {
    if (!enabled) return;
    let active = true;
    setRows(null);
    setError(null);
    fetch(proofUrl, { headers: { authorization: `Bearer ${token}` } })
      .then(async (r) => {
        if (!r.ok) throw new Error(`Failed to load (${r.status})`);
        return r.json();
      })
      .then((b) => {
        if (!active) return;
        setSummary(b.summary);
        setRows(b.data ?? []);
        setTotal(b.pagination?.total ?? (b.data ?? []).length);
      })
      .catch((e) => active && setError(e instanceof Error ? e.message : 'Failed to load'));
    return () => {
      active = false;
    };
  }, [enabled, proofUrl, token]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await fetch(csvUrl, {
        headers: { authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Export failed (${res.status})`);
      const blob = await res.blob();
      const match = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '');
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = match?.[1] ?? 'chargeback-proof.csv';
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  if (!entitlements.chargebackProof) {
    return (
      <div className="mx-auto max-w-3xl">
        <Card>
          <EmptyState
            icon={ICONS.list}
            title="Chargeback proof-out"
            hint="This feature isn't enabled for your account. Contact CommissionSight to turn it on."
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageTabs tabs={chargebacksTabs(entitlements)} />
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Chargeback proof-out</h1>
          <p className="mt-1 text-sm text-neutral-500">
            Every chargeback across your loaded history, traced to the original payment — when the
            carrier first paid, how much, and from which statement file — with each policy's
            lifetime paid-vs-clawed totals.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <WorkspaceFilter value={workspaceId} onChange={changeWorkspace} />
          <div className="w-44">
            <Field label="Carrier">
              <select
                className={inputClass}
                value={carrierId}
                onChange={(e) => changeCarrier(e.target.value)}
              >
                <option value="">All carriers</option>
                {carriers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <div className="w-44">
            <Field label="From">
              <PeriodPicker value={from} onChange={changeFrom} placeholder="First period" />
            </Field>
          </div>
          <div className="w-44">
            <Field label="To">
              <PeriodPicker value={to} onChange={changeTo} placeholder="Latest period" />
            </Field>
          </div>
          {(from || to) && (
            <Button
              variant="ghost"
              onClick={() => {
                setFrom('');
                setTo('');
                setOffset(0);
              }}
            >
              All time
            </Button>
          )}
          <Button onClick={exportCsv} loading={exporting} disabled={!rows || rows.length === 0}>
            Export CSV
          </Button>
        </div>
      </div>

      {error && <p className="rounded bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p>}

      {summary && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          <Card>
            <p className="text-xs text-neutral-500">Chargebacks</p>
            <p className="mt-1 text-2xl font-semibold">{summary.chargebacks}</p>
            <p className="mt-0.5 text-xs text-neutral-400">
              {summary.from && summary.to
                ? `${formatPeriodLong(summary.from)} – ${formatPeriodLong(summary.to)}`
                : 'no data'}
            </p>
          </Card>
          <Card>
            <p className="text-xs text-neutral-500">Clawed back</p>
            <p className="mt-1 text-2xl font-semibold text-amber-600 dark:text-amber-400">
              {formatCurrency(summary.clawedBack)}
            </p>
            <p className="mt-0.5 text-xs text-neutral-400">
              across {summary.policies} {summary.policies === 1 ? 'policy' : 'policies'}
            </p>
          </Card>
          <Card>
            <p className="text-xs text-neutral-500">Proven paid first</p>
            <p className="mt-1 text-2xl font-semibold text-emerald-600 dark:text-emerald-400">
              {summary.provenPaid}
            </p>
            <p className="mt-0.5 text-xs text-neutral-400">
              {summary.chargebacks > 0
                ? `${Math.round((summary.provenPaid / summary.chargebacks) * 100)}% of chargebacks`
                : '—'}
            </p>
          </Card>
          <Card>
            <p className="text-xs text-neutral-500">No payment in loaded data</p>
            <p className="mt-1 text-2xl font-semibold text-rose-600 dark:text-rose-400">
              {summary.neverPaid}
            </p>
            <p className="mt-0.5 text-xs text-neutral-400">
              {summary.paidAfter > 0 ? `+ ${summary.paidAfter} paid only after` : 'verify uploads'}
            </p>
          </Card>
          <Card>
            <p className="text-xs text-neutral-500">Reversals</p>
            <p className="mt-1 text-2xl font-semibold">{summary.fullyReversed}</p>
            <p className="mt-0.5 text-xs text-neutral-400">
              full{summary.overReversed > 0 ? ` · ${summary.overReversed} over-reversed` : ''}
            </p>
          </Card>
        </div>
      )}

      {summary?.truncated && (
        <p className="rounded bg-amber-50 px-3 py-2 text-sm text-amber-700 dark:bg-amber-950/40 dark:text-amber-300">
          Showing the first {summary.chargebacks} chargebacks — narrow the period range or filter by
          carrier to see the rest.
        </p>
      )}

      <Card className="p-0">
        <div className="grid grid-cols-[0.9fr_1.5fr_1fr_0.9fr_0.9fr_1.6fr_1.3fr] gap-4 border-b border-neutral-200 px-5 py-2 text-xs font-medium text-neutral-500 dark:border-neutral-800">
          <span>Month</span>
          <span>Member / policy</span>
          <span>Carrier</span>
          <span className="text-right">Clawed back</span>
          <span className="text-right">Verdict</span>
          <span>Original payment</span>
          <span className="text-right">Policy paid / clawed</span>
        </div>
        {!rows ? (
          <Loading label="Building proof-out…" />
        ) : rows.length === 0 ? (
          <p className="py-12 text-center text-sm text-neutral-500">
            No chargebacks in the selected range.
          </p>
        ) : (
          <div className="divide-y divide-neutral-100 dark:divide-neutral-800">
            {rows.map((r, i) => (
              <div
                key={`${r.period}-${r.policyRefId}-${i}`}
                className="grid grid-cols-[0.9fr_1.5fr_1fr_0.9fr_0.9fr_1.6fr_1.3fr] items-center gap-4 px-5 py-3 text-sm"
              >
                <div className="text-xs text-neutral-500">{formatPeriodLong(r.period)}</div>
                <div className="min-w-0">
                  <Link
                    to={`/members/${r.memberRefId}`}
                    className="truncate font-medium text-emerald-700 hover:underline dark:text-emerald-400"
                    title="View this member's full history"
                  >
                    {r.memberName ?? r.memberExternalId ?? r.memberRefId}
                  </Link>
                  <Link
                    to={`/policies/${r.policyRefId}`}
                    className="block truncate text-xs text-neutral-400 hover:text-emerald-600 hover:underline"
                  >
                    {r.policyNumber ?? '—'}
                    {r.planName ? ` · ${r.planName}` : ''}
                  </Link>
                </div>
                <div className="truncate text-xs text-neutral-500">
                  {r.carrierName ?? r.carrierId}
                </div>
                <div className="text-right font-semibold text-amber-600 dark:text-amber-400">
                  −{formatCurrency(r.chargebackAmount)}
                </div>
                <div className="text-right">
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs font-medium ${VERDICT_BADGE[r.verdict].className}`}
                  >
                    {VERDICT_BADGE[r.verdict].label}
                  </span>
                </div>
                <div className="min-w-0 text-xs">
                  {r.originalPayout ? (
                    <>
                      <div className="text-neutral-700 dark:text-neutral-300">
                        {formatCurrency(r.originalPayout.amount)} ·{' '}
                        {formatPeriodLong(r.originalPayout.period)}
                        {r.reversal && (
                          <span className="ml-1.5 text-neutral-400">({r.reversal})</span>
                        )}
                      </div>
                      <div className="truncate text-neutral-400">
                        {r.originalPayout.fileName ?? r.originalPayout.fileId ?? '—'}
                      </div>
                    </>
                  ) : (
                    <span className="text-rose-600 dark:text-rose-400">
                      No payment in loaded statements
                    </span>
                  )}
                </div>
                <div className="text-right text-xs text-neutral-500">
                  <span className="text-emerald-600 dark:text-emerald-400">
                    {formatCurrency(r.policyTotals.paid)}
                  </span>{' '}
                  /{' '}
                  <span className="text-amber-600 dark:text-amber-400">
                    {formatCurrency(r.policyTotals.chargedBack)}
                  </span>
                  <div className="text-neutral-400">
                    net {formatCurrency(r.policyTotals.net)} · {r.policyTotals.paymentCount}{' '}
                    {r.policyTotals.paymentCount === 1 ? 'payment' : 'payments'}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        {rows && total > PAGE_SIZE && (
          <div className="flex items-center justify-between border-t border-neutral-200 px-5 py-3 dark:border-neutral-800">
            <span className="text-xs text-neutral-500">
              Showing {total === 0 ? 0 : offset + 1}–{offset + rows.length} of {total} chargebacks —
              the CSV export always includes all {total}.
            </span>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              >
                ← Previous
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={offset + rows.length >= total}
                onClick={() => setOffset(offset + PAGE_SIZE)}
              >
                Next →
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
